import { useCallback, useState } from 'react';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { Dialog, Popover } from '@material-ui/core';
import trI18n from '@/common/utils/trI18n';
import ButtonOption from '@/component/common/ButtonOption';
import FlagMessageCopyLink from '@/component/common/FlagMessageCopyLink';
import EmailImageAttachment from './EmailImageAttachment';
import EmailOtherAttachment from './EmailOtherAttachment';
import { PropTypes } from 'prop-types';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { isTranslatableFileName } from '@/common/utils/translationJobUtils';
import FileTranslationDialog from '@/component/common/fileTranslation/FileTranslationDialog';
import CorporationUtils from '@/common/utils/CorporationUtils';
import { useRecoilValue } from 'recoil';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import useAttachThumbnailSize from '@/component/common/AttachFileThumbnail/handler/useAttachThumbnailSize';
import { THUMBNAIL_SIZE_CLIENT_CONTROL } from '@/component/common/icons/AttachUtils';
import useTranslatedAttachmentDisplay, { ATTACHMENT_DISPLAY_CONTEXT } from '@/hooks/useTranslatedAttachmentDisplay';

function EmailAttachmentViewOnly({ fieldData, detailTicket, setIsShowPopupLogout }) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();
    

    const [openPreviewImage, setOpenPreviewImage] = useState(null);
    const [openOption, setOpenOption] = useState(null);
    const [openFileOption, setOpenFileOption] = useState(null);
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);
    // Kept apart from openFileOption, which the Popover resets once it has closed.
    const [translateTargetFile, setTranslateTargetFile] = useState(null);

    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);

    const thumbnailSize = useAttachThumbnailSize(THUMBNAIL_SIZE_CLIENT_CONTROL.TICKET_DETAIL);

    // CR-303134: with the email's「翻訳版」toggle ON, a file shows its translation in the viewer's
    // main language. EmailOtherAttachment then finds the translation's own AttachFile record by
    // fileUrl, so ⋮ only offers「URLを取得」with the translation's share link (翻訳 needs attachBy = ticket).
    const { getDisplayFile } = useTranslatedAttachmentDisplay({
        attachFiles: detailTicket?.attachFiles,
        context: ATTACHMENT_DISPLAY_CONTEXT.DETAIL,
        contentId: detailTicket?.id,
        displayMode: Constant.DISPLAY_MODE.VIEW
    });

    // BR-10: an email ticket is a ticket like any other, and this component is its view mode.
    const isDisplayTranslateFileOption = attachFile =>
        attachFile?.attachBy === Constant.ATTACH_FILE_TYPE.TICKET &&
        isTranslatableFileName(attachFile?.fileInfo?.fileName) &&
        CorporationUtils.isEnableSettingFileTranslationAttachment(corporationDetail, projectInuseDetail);;

    const handleOpenTranslateDialog = () => {
        setTranslateTargetFile(openFileOption);
        setOpenOption(null);
    };

    const onClickCopyShareLink = useCallback(() => {
        const linkUrl = Utils.getShareLinkImg({
            attachFile: openFileOption,
            dataShareLink: detailTicket.dataShareLink
        });

        navigator.clipboard.writeText(linkUrl);
        setIsShowFlagMessage(true);
        setTimeout(() => {
            setIsShowFlagMessage(false);
        }, 3000);
    }, [detailTicket, openFileOption]);

    let fileList = fieldData.value;
    let fileImageList = fileList.filter(item => Utils.isImageFile(item.fileName));
    let otherFileList = fileList.filter(item => !Utils.isImageFile(item.fileName));

    return (
        <div className="lstName-file attach-file-chat-post-list">
            <ul>
                <div
                    style={{
                        display: 'flex',
                        columnGap: 10,
                        rowGap: 10,
                        flexWrap: 'wrap',
                        alignItems: 'flex-start'
                    }}
                >
                    {fileImageList.map((item, index) => (
                        <EmailImageAttachment
                            key={index}
                            item={item}
                            detailTicket={detailTicket}
                            thumbnailSize={thumbnailSize}
                            setOpenPreviewImage={setOpenPreviewImage}
                            setOpenFileOption={setOpenFileOption}
                            setOpenOption={setOpenOption}
                        />
                    ))}
                    {otherFileList.map((file, index) => (
                        <EmailOtherAttachment
                            key={index}
                            file={getDisplayFile(file)}
                            detailTicket={detailTicket}
                            thumbnailSize={thumbnailSize}
                            setIsShowPopupLogout={setIsShowPopupLogout}
                            setOpenFileOption={setOpenFileOption}
                            setOpenOption={setOpenOption}
                        />
                    ))}
                </div>
            </ul>
            <Popover
                open={Boolean(openOption) || false}
                anchorEl={openOption}
                onClose={() => {
                    setOpenOption(null);
                    setIsShowFlagMessage(false);
                }}
                TransitionProps={{
                    onExited: () => {
                        setOpenFileOption(null);
                    }
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {openFileOption?.shortKey && !Utils.isEmptyObject(detailTicket?.dataShareLink) && (
                        <FlagMessageCopyLink
                            title={trI18n('KNOWDEMSG002')}
                            placement="top-start"
                            arrow={true}
                            open={isShowFlagMessage}
                        >
                            <ButtonOption onClick={onClickCopyShareLink}>
                                <span
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        fontFamily: 'Zen Kaku Gothic New'
                                    }}
                                >
                                    {trI18n('GET_URL')}
                                </span>
                            </ButtonOption>
                        </FlagMessageCopyLink>
                    )}

                    {isDisplayTranslateFileOption(openFileOption) && (
                        <ButtonOption onClick={handleOpenTranslateDialog}>
                            <span
                                style={{
                                    fontSize: 15,
                                    fontWeight: 400,
                                    fontFamily: 'Zen Kaku Gothic New'
                                }}
                            >
                                {trI18n('LABEL-TRANSLATE-FILE-MENU')}
                            </span>
                        </ButtonOption>
                    )}
                </div>
            </Popover>

            {translateTargetFile && (
                <FileTranslationDialog
                    isOpen={Boolean(translateTargetFile)}
                    onClose={() => setTranslateTargetFile(null)}
                    file={translateTargetFile}
                    detailTicket={detailTicket}
                />
            )}

            <Dialog
                open={!!openPreviewImage}
                onClose={() => setOpenPreviewImage(null)}
                maxWidth="md"
            >
                <img
                    alt=""
                    src={openPreviewImage}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain'
                    }}
                />
            </Dialog>
        </div>
    );
}

EmailAttachmentViewOnly.propTypes = {
    fieldData: PropTypes.any,
    detailTicket: PropTypes.any,
    setIsShowPopupLogout: PropTypes.func
};

export default EmailAttachmentViewOnly;

